import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useDialog, useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import type { Brand } from "~/types/api";

type BrandsMutationContext = {
  previousBrands: Brand[] | undefined;
  deletedBrand: Brand | undefined;
};

export const useDeleteBrand = () => {
  const { closeDialog, showDialog } = useDialog();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const brandsQueryOptions = configurationApi.queryOptions("get", "/api/Brand");
  const brandsQueryKey = brandsQueryOptions.queryKey;

  const { mutateAsync } = configurationApi.useMutation("delete", "/api/Brand/{brandId}", {
    onMutate: async (variables) => {
      const brandIdToDelete = variables.params.path.brandId;
      await queryClient.cancelQueries({ queryKey: brandsQueryKey });
      const previousBrands = queryClient.getQueryData<Brand[]>(brandsQueryKey);

      let deletedBrand: Brand | undefined;

      queryClient.setQueryData<Brand[]>(brandsQueryKey, (oldData) => {
        if (!Array.isArray(oldData)) return oldData;
        deletedBrand = oldData.find((brand) => brand.id === brandIdToDelete);
        return oldData.filter((brand) => brand.id !== brandIdToDelete);
      });

      return { previousBrands, deletedBrand } as BrandsMutationContext;
    },

    onError: (_err, _variables, context) => {
      const errorMessage = _err instanceof Error ? _err.message : "An unknown error occurred.";
      setError(errorMessage);
      showToast("error", "Failed to delete brand. Please try again.");
      const ctx = context as BrandsMutationContext;

      const previousBrands = ctx.previousBrands;
      const deletedBrand = ctx.deletedBrand;

      if (previousBrands && deletedBrand) {
        queryClient.setQueryData(brandsQueryKey, previousBrands);
      }
    },

    onSuccess: () => {
      setError(null);
      showToast("success", "Brand deleted successfully.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: brandsQueryKey });
    },
  });

  const deleteBrand = (brandId: number, hasActiveTasks: boolean) => {
    setError(null);

    showDialog({
      title: "Brand Deletion",
      description: hasActiveTasks
        ? "Brand with active tasks can't be deleted"
        : "Are you sure you want to delete this brand?",
      confirmText: hasActiveTasks ? undefined : "Delete",
      confirmVariant: "destructive",
      cancelVariant: "outline",
      cancelText: "Go Back",
      onConfirm: async () => {
        try {
          if (!hasActiveTasks) {
            await mutateAsync({ params: { path: { brandId } } });
          }
          closeDialog();
        } catch (err) {
          console.error("Error deleting brand:", err);
        }
      },
    });
  };

  return { deleteBrand, error };
};
